import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { OfficeRoamingSettings } from "../../services/OfficeRoamingSettings";
import { NewformaToastIntent } from "../_base/Toast";
import { SuggestedProject } from "../../services/SmartFiling/SuggestedProject";
import { SmartFilingManagerFluent9 } from "../../services/SmartFiling/SmartFilingManagerFluent9";
import { OfficeNotificationService } from "../../services/officeUi/OfficeNotificationService";
import { ExpiredSessionError } from "../../models/ExpiredSessionError";
import uuid from "uuid";
import { MailboxItem, OfficeWrapper } from "../../services/OfficeWrapper";
import { AnalyticsActionType, AnalyticsCategoryType, AnalyticsManager } from "../../services/AnalyticsManager";
import InfoComponent from "../shared/info/InfoComponent";
import { InternetHeader } from "../../models/InternetHeader";
import { Logger, Log } from "../../services/Logger";
import { SendAndFileHelpers } from "../../helpers/SendAndFile/SendAndFileHelpers";
import { AppPage } from "../shared/navigationHeader/NavigationHeaderComponent";
import { EmailApiService } from "../../services/NewformaApi/EmailApiService";
import { FilingLocation } from "../../models/FilingLocationsResponse";
import { ProjectsApiService } from "../../services/NewformaApi/ProjectsApiService";
import { Project, ProjectsResponse } from "../../models/ProjectsResponse";
import { Paging } from "../../models/ProjectKeywordsResponse";
import { IProjectsService } from "../../services/NewformaApi/IProjectsService";
import { InvalidateCacheService } from "../../services/NewformaApi/InvalidateCacheService";
import { ProjectsCacheKeys } from "../../models/StorageKeys";
import { MsGraphApiService, waitForNewformaFolderId } from "../../services/MsGraphApiService";
import ProjectRadiosFluent9 from "./ProjectRadiosFluent9";
import { NewformaLink } from "../_base/Link";
import { AuthApiService } from "../../services/NewformaApi/AuthApiService";
import { setLocalStorageItem } from "../../components/app/AppComponent";
import { IProjectData } from "../../models/shared/IProjectData";
import SendAndFileProjectSearch from "./SendAndFileProjectSearch";
import SendAndFilePaginatedProjectArea from "./SendAndFilePaginatedProjectArea";
import { makeStyles, Text, tokens } from "@fluentui/react-components";
import { ArrowClockwise20Regular, Info16Regular } from "@fluentui/react-icons";
import { NewformaSwitch } from "../_base/NewformaSwitch";
import { NewformaFolderTreeNode } from "../_base/NewformaFolderTree";
import SubfolderPickerFluent9 from "../shared/subfolderPicker/SubfolderPickerFluent9";
import { FOLDER_FETCH_PAGE_MAX_SIZE } from "../../constants/projectFiles";
import { buildEmailFolderNrn } from "../../helpers/NrnHelpers";
import { ProjectHelper } from "../../helpers/ProjectHelper";

export interface SendAndFileFluent9Props extends LocalizeContextProps {
    officeRoamingSettings: OfficeRoamingSettings;
    smartFilingManager: SmartFilingManagerFluent9;
    onExpiredSession: () => void;
    officeWrapper: OfficeWrapper;
    officeNotificationService: OfficeNotificationService;
    analyticsManager: AnalyticsManager;
    logger: Logger;
    sendAndFileHelpers: SendAndFileHelpers;
    onSetNavigationPage: (page: AppPage) => void;
    onShowToast: (toastMessage: string | null, toastType: NewformaToastIntent) => void;
    mailboxItem: MailboxItem | null;
    emailApiService: EmailApiService;
    projectApiService: ProjectsApiService;
    invalidateCacheService: InvalidateCacheService;
    projectsService: IProjectsService;
    msGraphApiService: MsGraphApiService;
    useGlobalProjects: boolean;
    theme: string;
    authApiService: AuthApiService;
    culture?: string;
    forceEmailFilingSetting?: boolean;
    isGlobalProjectSearchSupported: boolean;
    isFileEmailIntoSubfolderSupported: boolean;
}

const requiredVersion = "1.8";
const componentName = "SendAndFileFluent9";
const MAX_VISIBLE_PROJECTS = 15;
const SUBFOLDER_SORT_BY = "asc";

const useStyles = makeStyles({
    root: {
        display: "grid",
        gridTemplateRows: "min-content minmax(0, 1fr) min-content",
        flexGrow: 1,
        minHeight: 0,
        padding: "0px 25px 10px 25px",
        boxSizing: "border-box",
        fontFamily: "Segoe UI",
        height: "100%",
        overflow: "hidden",
        rowGap: "0px",
        backgroundColor: tokens.colorNeutralBackground1,
    },
    reloadTopLabel: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightRegular,
    },
    searchContainer: {
        fontSize: tokens.fontSizeBase300,
    },
    searchInputRow: {
        marginTop: "8px",
    },
    searchHintRow: {
        marginTop: "4px",
    },
    searchSubtextSimple: {
        marginTop: "4px",
    },
    searchSubtextWithIcon: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        marginTop: "4px",
    },
    refreshLinkRow: {
        display: "flex",
        marginTop: tokens.spacingVerticalS,
        marginBottom: tokens.spacingVerticalS,
        marginLeft: "auto",
    },
    clearButton: {
        marginTop: tokens.spacingVerticalM,
        marginBottom: tokens.spacingVerticalM,
        display: "inline-block",
        width: "fit-content",
        alignSelf: "end",
    },
    refreshLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: tokens.fontSizeBase200,
        marginLeft: "auto",
    },
    subfolderSection: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalS,
        width: "100%",
        alignSelf: "end",
        marginTop: "4px",
        paddingTop: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalS,
        boxSizing: "border-box",
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    subfolderPicker: {
        marginBottom: "4px",
    },
    subfolderSwitch: {
        width: "100%",
        justifyContent: "space-between",
        "& .fui-Switch__label": {
            paddingLeft: 0,
            paddingRight: 0,
            cursor: "default",
        },
        "& .fui-Switch__label *": {
            cursor: "default",
        },
        "& .fui-Switch__label .fui-Text": {
            fontSize: tokens.fontSizeBase300,
            fontWeight: tokens.fontWeightSemibold,
        },
        "& .fui-Switch__indicator": {
            transformOrigin: "right center",
        },
    },
});

const SendAndFileFluent9 = (props: SendAndFileFluent9Props): JSX.Element => {
    const styles = useStyles();
    const isMailboxVersionSupported = props.officeWrapper.isMailboxVersionSupported(requiredVersion);

    const [isLoadingMyProjects, setIsLoadingMyProjects] = useState(false);
    const [isLoadingGlobalProjects, setIsLoadingGlobalProjects] = useState(false);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [myProjects, setMyProjects] = useState<Project[]>([]);
    const [globalProjects, setGlobalProjects] = useState<Project[]>([]);
    const [suggestedProjects, setSuggestedProjects] = useState<Project[]>([]);
    const [suggestedProjectsAsSuggestedProjectInterface, setSuggestedProjectsAsSuggestedProjectInterface] = useState<
        SuggestedProject[]
    >([]);
    const [filingLocations, setFilingLocations] = useState<FilingLocation[]>([]);
    const [projectFilterString, setProjectFilterString] = useState("");
    const [serverSearchResults, setServerSearchResults] = useState<Project[]>([]);
    const [isServerSearching, setIsServerSearching] = useState(false);
    const [isSubfolderEnabled, setIsSubfolderEnabled] = useState(false);
    const [subfolders, setSubfolders] = useState<NewformaFolderTreeNode[]>([]);
    const [selectedSubfolder, setSelectedSubfolder] = useState<NewformaFolderTreeNode | undefined>(undefined);
    const [isReloadingSubfolders, setIsReloadingSubfolders] = useState(false);
    const subfolderRequestIdRef = useRef(0);
    const fetchIdRef = useRef(0);
    const hasRetriedEmptyMyProjectsRef = useRef(false);
    const hasLoadedOnceRef = useRef(false);
    const isUserReloadRef = useRef(false);
    const isInitialRenderRef = useRef(true);
    const hasMountedRef = useRef(false);
    const onProjectSelectionChangedRef = useRef<
        (selectedProject: Project | null, suggestedProjectsList?: SuggestedProject[]) => Promise<void>
    >(async () => {});

    const clearEmailBanner = useCallback(() => {
        Log.info("Clearing Banner");
        props.officeNotificationService.dismissInfoToast(props.mailboxItem, "newforma-info-message");
    }, [props.mailboxItem, props.officeNotificationService]);

    const setEmailBanner = useCallback(
        (message: string) => {
            props.officeNotificationService.dismissInfoToast(props.mailboxItem, "newforma-info-message");
            setTimeout(() => {
                props.officeNotificationService.showInfoToast(props.mailboxItem, "newforma-info-message", message);
            }, 100);
        },
        [props.mailboxItem, props.officeNotificationService]
    );

    const setIsSendAndFileProjectSelectedLocalStorage = useCallback((isSelected: boolean) => {
        Log.info(`setting isSendAndFileProjectSelected = ${isSelected}`);
        try {
            setLocalStorageItem("isSendAndFileProjectSelected", isSelected);
        } catch (error) {
            Log.info("Error setting isSendAndFileProjectSelected");
        }
    }, []);

    const getEmailIdInfo = useCallback(async () => {
        const emailId = await props.officeWrapper.getInternetHeader(InternetHeader.EmailId);
        return {
            emailId: emailId ? emailId : uuid(),
            isEmailIdExisting: !!emailId,
        };
    }, [props.officeWrapper]);

    const recordFilingForSuggestedProjectSmartFiling = useCallback(
        async (selectedProject: FilingLocation, suggestedProjectsList: SuggestedProject[]) => {
            const suggestedProject = suggestedProjectsList.find((project) => project.nrn === selectedProject.nrn);
            const suggestedProjectIndex = suggestedProject
                ? suggestedProjectsList.indexOf(suggestedProject)
                : undefined;
            props.analyticsManager.recordSmartFilingEvents(suggestedProject, suggestedProjectIndex);
        },
        [props.analyticsManager]
    );

    const recordFilingForFilingHistory = useCallback(
        async (selectedProject: FilingLocation) => {
            Log.info("addToFiledHistory");
            if (!props.mailboxItem) {
                return;
            }
            await props.smartFilingManager.addToFiledHistory(
                { key: selectedProject.nrn, name: selectedProject.name },
                props.mailboxItem.conversationId,
                props.officeWrapper.userProfileEmailAddress
            );
        },
        [props.mailboxItem, props.officeWrapper.userProfileEmailAddress, props.smartFilingManager]
    );

    const createFolderForProject = useCallback(
        async (displayName: string): Promise<FilingLocation | undefined> => {
            const parentFolderId = await waitForNewformaFolderId();
            if (!parentFolderId) {
                Log.error("createFolder - newformaFolderId is not available.");
                props.onShowToast(props.translate("SEND_AND_FILE.ERROR") as string, "error");
                return;
            }
            const folderId = (await props.msGraphApiService.createSubfolder(parentFolderId, displayName)) ?? "";
            if (!folderId) {
                Log.error("createFolder - Error creating folder.");
                props.onShowToast(props.translate("SEND_AND_FILE.ERROR") as string, "error");
                return;
            }
            return { name: displayName, nrn: `nrn:email:folder:ms-graph:${encodeURIComponent(folderId)}` };
        },
        [props.msGraphApiService, props.onShowToast, props.translate]
    );

    const addMetaTagToFolder = useCallback(
        async (folder: FilingLocation) => {
            const folderId = folder.nrn.split(":")[4];
            if (!folderId) {
                Log.error("addMetaTagToFolder - folderId doesn't exist.");
                props.onShowToast(props.translate("SEND_AND_FILE.ERROR") as string, "error");
                return;
            }
            await props.msGraphApiService.setFolderExtendedProperty(folderId, "timestamp", new Date().toISOString());
            props.msGraphApiService
                .getFolderExtendedProperty(folderId, "timestamp")
                .then((savedTimestamp) =>
                    Log.info(`Updated folder ${folder?.name ?? ""}, Confirmed Time Stamp= ${savedTimestamp}`)
                )
                .catch((error) => Log.error(`Failed to update timestamp for folder ${folder?.name ?? ""}`, error));
        },
        [props.msGraphApiService, props.onShowToast, props.translate]
    );

    const clearSelectedProject = useCallback(async (): Promise<void> => {
        clearEmailBanner();
        await props.officeWrapper.removeInternetHeaders([InternetHeader.EmailId, InternetHeader.FilingLocationNrn]);
    }, [clearEmailBanner, props.officeWrapper]);

    const getFilingFolder = useCallback(
        async (selectedProject: Project): Promise<FilingLocation | null> => {
            const displayName: string = selectedProject.autoFileMailboxFolderName ?? selectedProject.name ?? "";
            const existingFolder = filingLocations.find((f) => f.name === displayName);
            if (existingFolder) {
                return existingFolder;
            }
            const newFolder = await createFolderForProject(displayName);
            if (!newFolder) {
                return null;
            }
            setFilingLocations((prev) => [...prev, newFolder]);
            return newFolder;
        },
        [createFolderForProject, filingLocations]
    );

    const markEmailForProject = useCallback(
        async (
            selectedProject: Project,
            folder: FilingLocation,
            suggestedProjectsList: SuggestedProject[]
        ): Promise<void> => {
            // Run addMetaTagToFolder (Graph API) and getEmailIdInfo (Office.js COM)
            // in parallel since they are independent.
            const [, { emailId, isEmailIdExisting }] = await Promise.all([
                addMetaTagToFolder(folder).catch((error) => Log.error("addMetaTagToFolder failed", error)),
                getEmailIdInfo(),
            ]);

            // Yield to the browser between COM calls so the UI remains responsive
            // in Classic Outlook's Trident webview where COM calls block the thread.
            await new Promise((resolve) => setTimeout(resolve, 0));

            await props.officeWrapper.setInternetHeaders({
                [InternetHeader.EmailId]: emailId,
                [InternetHeader.FilingLocationNrn]: folder.nrn,
            });

            if (!isEmailIdExisting) {
                await Promise.all([
                    props.sendAndFileHelpers.updateRecipientsWithProjectEmailAddress(folder),
                    props.emailApiService.markEmailForFiling(emailId, props.culture),
                    recordFilingForSuggestedProjectSmartFiling(selectedProject, suggestedProjectsList),
                    recordFilingForFilingHistory(selectedProject),
                ]);

                props.analyticsManager.recordEvent(
                    AnalyticsCategoryType.UserActions,
                    AnalyticsActionType.EmailMarkedForFiling
                );
            }
        },
        [
            addMetaTagToFolder,
            getEmailIdInfo,
            props.analyticsManager,
            props.culture,
            props.emailApiService,
            props.officeWrapper,
            props.sendAndFileHelpers,
            recordFilingForFilingHistory,
            recordFilingForSuggestedProjectSmartFiling,
        ]
    );

    const showMarkedForFileBanner = useCallback(
        (folder: FilingLocation) => {
            const successMessage = String(props.translate("SEND_AND_FILE.MARKED_FOR_FILE_TOAST")).replace(
                /\[\[project-name]]/gi,
                folder.name
            );
            Log.info(`markFile ${successMessage}`);
            setEmailBanner(successMessage);
        },
        [props.translate, setEmailBanner]
    );

    // Creates (or finds, if it already exists) the real Outlook subfolder chain nested inside the
    // project's own filing folder, so the marked-for-send email targets the leaf folder directly.
    const getNestedSubfolder = useCallback(
        async (parentFolder: FilingLocation, subfolderNamePath: string[]): Promise<FilingLocation | undefined> => {
            // const parentFolderId = parentFolder.nrn.split(":")[4];
            const encodedParentFolderId = parentFolder.nrn.split(":")[4];
            const parentFolderId = encodedParentFolderId ? decodeURIComponent(encodedParentFolderId) : "";
            if (!parentFolderId) {
                Log.error("getNestedSubfolder - parentFolderId doesn't exist.");
                props.onShowToast(props.translate("SEND_AND_FILE.ERROR") as string, "error");
                return;
            }
            const folderId =
                (await props.msGraphApiService.createNestedSubfolders(parentFolderId, subfolderNamePath)) ?? "";
            if (!folderId) {
                Log.error("getNestedSubfolder - Error creating subfolder chain.");
                props.onShowToast(props.translate("SEND_AND_FILE.ERROR") as string, "error");
                return;
            }
            const leafName = subfolderNamePath[subfolderNamePath.length - 1];
            return { name: leafName, nrn: `nrn:email:folder:ms-graph:${encodeURIComponent(folderId)}` };
        },
        [props.msGraphApiService, props.onShowToast, props.translate]
    );

    const getEffectiveFilingFolder = useCallback(
        async (
            projectFolder: FilingLocation,
            subfolder: NewformaFolderTreeNode | undefined
        ): Promise<FilingLocation> => {
            if (!subfolder) {
                return projectFolder;
            }
            const subfolderNamePath = ProjectHelper.getFolderNamePath(subfolders, subfolder.id) ?? [subfolder.name];
            const nestedFolder = await getNestedSubfolder(projectFolder, subfolderNamePath);
            return nestedFolder ?? projectFolder;
        },
        [getNestedSubfolder, subfolders]
    );

    const isCurrentSubfolderRequest = useCallback(
        (requestId: number) => requestId === subfolderRequestIdRef.current,
        []
    );

    const loadSubfolders = useCallback(
        async (nrn: string): Promise<void> => {
            const requestId = ++subfolderRequestIdRef.current;
            setIsReloadingSubfolders(true);

            try {
                const rootFolder = await props.projectsService.getProjectFiles(
                    nrn,
                    FOLDER_FETCH_PAGE_MAX_SIZE,
                    false,
                    true,
                    SUBFOLDER_SORT_BY,
                    true
                );

                const rootItem = rootFolder?.items?.[0];
                if (!rootItem) {
                    if (isCurrentSubfolderRequest(requestId)) {
                        setSubfolders([]);
                    }
                    return;
                }

                const emailFolderNrn = buildEmailFolderNrn(rootItem.nrn, nrn);
                const response = await props.projectsService.getProjectFiles(
                    emailFolderNrn,
                    FOLDER_FETCH_PAGE_MAX_SIZE,
                    true,
                    true,
                    SUBFOLDER_SORT_BY,
                    true
                );

                if (isCurrentSubfolderRequest(requestId)) {
                    setSubfolders(ProjectHelper.mapProjectFolders(response?.items));
                }
            } catch (error) {
                if (isCurrentSubfolderRequest(requestId)) {
                    setSubfolders([]);
                    props.logger.error("Error loading project subfolders", error);
                }
            } finally {
                if (isCurrentSubfolderRequest(requestId)) {
                    setIsReloadingSubfolders(false);
                }
            }
        },
        [props.logger, props.projectsService, isCurrentSubfolderRequest]
    );

    const onProjectSelectionChanged = useCallback(
        async (selectedProject: Project | null, suggestedProjectsList: SuggestedProject[] = []): Promise<void> => {
            Log.info(`Selected project: ${JSON.stringify(selectedProject)}`);
            setSelectedProject(selectedProject);
            subfolderRequestIdRef.current += 1;
            setIsSubfolderEnabled(false);
            setSelectedSubfolder(undefined);
            setSubfolders([]);
            setIsReloadingSubfolders(false);

            setIsSendAndFileProjectSelectedLocalStorage(!!selectedProject);

            if (!selectedProject) {
                await clearSelectedProject();
                return;
            }

            const folder = await getFilingFolder(selectedProject);
            if (!folder) {
                return;
            }

            await markEmailForProject(selectedProject, folder, suggestedProjectsList);
            showMarkedForFileBanner(folder);

            void loadSubfolders(selectedProject.nrn);
        },
        [
            clearSelectedProject,
            getFilingFolder,
            loadSubfolders,
            markEmailForProject,
            setIsSendAndFileProjectSelectedLocalStorage,
            showMarkedForFileBanner,
        ]
    );

    // Recomputes the marked-for-send target folder (project folder, or a nested subfolder when
    // one is enabled and selected) and re-applies it without disturbing the project selection.
    const applyFilingTarget = useCallback(
        async (subfolder: NewformaFolderTreeNode | undefined): Promise<void> => {
            if (!selectedProject) {
                return;
            }
            const projectFolder = await getFilingFolder(selectedProject);
            if (!projectFolder) {
                return;
            }
            const effectiveFolder = await getEffectiveFilingFolder(projectFolder, subfolder);
            await markEmailForProject(selectedProject, effectiveFolder, suggestedProjectsAsSuggestedProjectInterface);
            showMarkedForFileBanner(effectiveFolder);
        },
        [
            selectedProject,
            getFilingFolder,
            getEffectiveFilingFolder,
            markEmailForProject,
            suggestedProjectsAsSuggestedProjectInterface,
            showMarkedForFileBanner,
        ]
    );

    const updateFileLocations = useCallback(async () => {
        try {
            const folderId = await waitForNewformaFolderId();
            if (!folderId) {
                props.logger.info("updateFileLocations skipped: newformaFolderId not set after setup");
                return;
            }
            const filingLocations: FilingLocation[] = [];
            const folderIdList = await props.msGraphApiService.getSubfolder(folderId);
            if (Array.isArray(folderIdList)) {
                for (const folder of folderIdList) {
                    filingLocations.push({
                        name: folder.displayName,
                        nrn: `nrn:email:folder:ms-graph:${encodeURIComponent(folder.id)}`,
                    });
                }
            }
            setFilingLocations(filingLocations);
        } catch (error) {
            props.logger.error(`update file locations error with getting file locations: ${error}`);
        }
    }, [props.logger, props.msGraphApiService]);

    const loadMyProjects = useCallback(
        async (inFileOnSendRetry?: boolean): Promise<Project[]> => {
            Log.info(`${componentName} LoadMyProjects`);
            const globalProjectsOffsetToken = "globalProject";
            let myProjectsResponse: ProjectsResponse = {
                projects: [],
                paging: { offsetToken: globalProjectsOffsetToken },
            };
            try {
                Log.info(`${componentName} - loading My projects.`);

                myProjectsResponse = await props.projectsService.getMyProjects(inFileOnSendRetry);
                const mpCount = myProjectsResponse ? myProjectsResponse.projects?.length : 0;
                Log.info(`${componentName} - Received my projects. [ ${mpCount} ]`);

                return myProjectsResponse.projects;
            } catch (error) {
                Log.error(`${componentName} - error loading My projects`);
                return [];
            }
        },
        [props.projectsService]
    );

    const loadGlobalProjects = useCallback(
        async (paging?: Paging): Promise<void> => {
            if (!props.useGlobalProjects) {
                return;
            }
            fetchIdRef.current += 1;
            const currentFetchId = fetchIdRef.current;

            Log.info(` Start loading global projects. Id [${currentFetchId}] `);
            setIsLoadingGlobalProjects(true);
            const startTime = new Date();
            let cumulativeProjects: Project[] = [];
            try {
                let updatedPaging: Paging | undefined = paging || { offsetToken: "" };

                let pageCount = 0;
                do {
                    pageCount++;
                    Log.info(`Global ${updatedPaging} ${currentFetchId}`);
                    const globalProjectsResponse: ProjectsResponse = await props.projectsService.getGlobalProjects(
                        updatedPaging
                    );

                    if (fetchIdRef.current !== currentFetchId) {
                        Log.info("Re-Entrant - Terminate");
                        break;
                    }

                    cumulativeProjects = cumulativeProjects.concat(globalProjectsResponse.projects);

                    setGlobalProjects(cumulativeProjects);
                    updatedPaging = globalProjectsResponse.paging;
                    Log.info(`*** ${pageCount} : Project length ${cumulativeProjects.length}`);
                } while (updatedPaging?.offsetToken);
            } catch (error) {
                Log.error("Error loading global projects", error);
            } finally {
                if (fetchIdRef.current === currentFetchId) {
                    const diffTime = (Date.now() - startTime.getTime()) / 1000;
                    Log.info(`Loading all projects completed in ${diffTime.toString()} sec`);
                    setIsLoadingGlobalProjects(false);
                    isUserReloadRef.current = false;
                }
            }
            Log.info(`Done Id ${currentFetchId}, Total [${cumulativeProjects.length}]`);
        },
        [props.projectsService, props.useGlobalProjects]
    );

    // Keep a ref to the latest onProjectSelectionChanged to avoid dependency cycles
    onProjectSelectionChangedRef.current = onProjectSelectionChanged;

    const determineSuggestedProjects = useCallback(async () => {
        const allProjects = props.isGlobalProjectSearchSupported ? myProjects : myProjects.concat(globalProjects);

        const suggestedProjectsAsSuggestedProjectInterface = await props.smartFilingManager.getSuggestedProjects(
            allProjects.map((project) => ({ key: project.nrn, name: project.name })),
            undefined,
            true
        );

        const suggestedProjects = suggestedProjectsAsSuggestedProjectInterface
            .slice(0, 3)
            .map((sp) => myProjects.find((project) => project.nrn === sp.nrn))
            .filter((project) => !!project) as Project[];

        const preSelectedProject = allProjects.find((project) => project.nrn === suggestedProjects[0]?.nrn) ?? null;

        setSuggestedProjectsAsSuggestedProjectInterface(suggestedProjectsAsSuggestedProjectInterface);
        setSuggestedProjects(suggestedProjects);

        // Yield to the browser so the project list renders before the slow
        // Office.js setInternetHeaders call blocks the UI thread.
        await new Promise((resolve) => setTimeout(resolve, 0));

        await onProjectSelectionChangedRef.current(preSelectedProject, suggestedProjectsAsSuggestedProjectInterface);
    }, [globalProjects, myProjects, props.isGlobalProjectSearchSupported, props.smartFilingManager]);

    const loadProjects = useCallback(
        async (inFileOnSendRetry?: boolean) => {
            try {
                setIsLoadingMyProjects(true);
                try {
                    const [, myProjects] = await Promise.all([
                        updateFileLocations(),
                        loadMyProjects(inFileOnSendRetry),
                    ]);

                    setMyProjects(myProjects);
                    if (props.isGlobalProjectSearchSupported) {
                        setGlobalProjects(myProjects);
                        isUserReloadRef.current = false;
                    } else if (props.useGlobalProjects) {
                        void loadGlobalProjects();
                    } else {
                        setGlobalProjects(myProjects);
                        isUserReloadRef.current = false;
                    }
                } catch (error) {}
            } catch (error) {
                if (ExpiredSessionError.isInstanceOf(error)) {
                    props.onExpiredSession();
                } else {
                    props.logger.error("Error:", error as any);
                    props.onShowToast(props.translate("SEND_AND_FILE.ERROR") as string, "error");
                }
                isUserReloadRef.current = false;
            } finally {
                setIsLoadingMyProjects(false);
                hasLoadedOnceRef.current = true;
            }
        },
        [
            loadGlobalProjects,
            loadMyProjects,
            props.isGlobalProjectSearchSupported,
            props.logger,
            props.onExpiredSession,
            props.onShowToast,
            props.translate,
            props.useGlobalProjects,
            updateFileLocations,
        ]
    );

    useEffect(() => {
        if (!isMailboxVersionSupported) {
            return;
        }
        if (hasMountedRef.current) {
            return;
        }
        hasMountedRef.current = true;
        Log.info("SendAndFileNEWComponent mounted");
        try {
            setLocalStorageItem("isSendAndFileProjectSelected", false);
        } catch (error) {
            Log.info("Error setting isSendAndFileProjectSelected in mount");
        }
        props.onSetNavigationPage(AppPage.SendAndFile);
        void loadProjects();
    }, [isMailboxVersionSupported]);

    useEffect(() => {
        if (!isMailboxVersionSupported) {
            return;
        }
        if (myProjects.length > 0) {
            hasRetriedEmptyMyProjectsRef.current = false;
            return;
        }
        if (!hasLoadedOnceRef.current || isLoadingMyProjects) {
            return;
        }
        if (!hasRetriedEmptyMyProjectsRef.current) {
            Log.info("My projects array is empty and retrying loading my projects");
            hasRetriedEmptyMyProjectsRef.current = true;
            void (async () => {
                const retriedProjects = await loadMyProjects(true);
                setMyProjects(retriedProjects);
                Log.info(`My projects array after retry [${retriedProjects.length}]`);
            })();
        }
    }, [isMailboxVersionSupported, isLoadingMyProjects, loadMyProjects, myProjects]);

    useEffect(() => {
        if (!isMailboxVersionSupported) {
            return;
        }
        if (isInitialRenderRef.current) {
            isInitialRenderRef.current = false;
            return;
        }
        props.logger.info("useGlobalProjects changed");
        if (props.isGlobalProjectSearchSupported) {
            setGlobalProjects(myProjects);
        } else if (props.useGlobalProjects) {
            void loadGlobalProjects();
        } else {
            setGlobalProjects(myProjects);
        }
    }, [
        isMailboxVersionSupported,
        loadGlobalProjects,
        props.isGlobalProjectSearchSupported,
        props.logger,
        props.useGlobalProjects,
    ]);

    useEffect(() => {
        if (!isMailboxVersionSupported) {
            return;
        }
        void determineSuggestedProjects();
    }, [isMailboxVersionSupported, myProjects, props.mailboxItem]);

    const onRefresh = async (): Promise<void> => {
        Log.info("Refresh Start");
        isUserReloadRef.current = true;
        setProjectFilterString("");

        // Run clear-selection and cache invalidation in parallel — they are independent.
        const cacheInvalidations: Promise<void>[] = [
            props.invalidateCacheService.invalidateCache(ProjectsCacheKeys.myProjectsCacheName),
        ];
        if (props.useGlobalProjects && !props.isGlobalProjectSearchSupported) {
            cacheInvalidations.push(
                props.invalidateCacheService.invalidateCache(ProjectsCacheKeys.globalProjectsCacheName)
            );
        }
        await Promise.all([onProjectSelectionChanged(null), ...cacheInvalidations]);

        await loadProjects();
        Log.info("Refresh End");
    };

    const onSearchChange = (_event?: ChangeEvent<HTMLInputElement>, newValue?: string): void => {
        setProjectFilterString(newValue ?? "");
        clearSelectionIfNeeded();
    };

    const onSearchClear = (): void => {
        setProjectFilterString("");
        setServerSearchResults([]);
        setIsServerSearching(false);
        clearSelectionIfNeeded();
    };

    const clearSelectionIfNeeded = (): void => {
        if (selectedProject) {
            void onProjectSelectionChanged(null);
        }
    };

    const toServerSearchProject = (project: IProjectData): Project => ({
        nrn: project.nrn,
        name: project.name,
        code: project.code ?? "",
        number: project.number ?? "",
        capabilities: [],
        type: "",
        hasProjectFolder: false,
        nixEnabled: project.nixEnabled,
        autoFileMailboxFolderName: project.autoFileMailboxFolderName ?? project.name,
    });

    const onServerSearchResults = useCallback((results: IProjectData[], isSearching: boolean) => {
        const serverSearchResults: Project[] = results.map(toServerSearchProject);
        setServerSearchResults(serverSearchResults);
        setIsServerSearching(isSearching);
    }, []);

    const getProjectDisplayName = useCallback((project: Project): string => {
        return !!project.number ? `${project.number} - ${project.name}` : project.name;
    }, []);

    const getIsProjectFilterMatch = useCallback(
        (project: Project, filterString: string): boolean => {
            return getProjectDisplayName(project)
                .toLocaleLowerCase()
                .includes(filterString);
        },
        [getProjectDisplayName]
    );

    const handleProjectSelectionChanged = (project: Project | null): void => {
        void onProjectSelectionChanged(project, suggestedProjectsAsSuggestedProjectInterface);
    };

    const handleSubfolderToggleChanged = useCallback(
        (isChecked: boolean) => {
            setIsSubfolderEnabled(isChecked);
            setSelectedSubfolder(undefined);
            void applyFilingTarget(undefined);
        },
        [applyFilingTarget]
    );

    const handleSubfolderSelected = useCallback(
        (folder: NewformaFolderTreeNode | undefined) => {
            setSelectedSubfolder(folder);
            void applyFilingTarget(folder);
        },
        [applyFilingTarget]
    );

    const handleReloadSubfolders = useCallback(() => {
        if (!selectedProject) {
            return;
        }
        setSelectedSubfolder(undefined);
        void applyFilingTarget(undefined);
        void loadSubfolders(selectedProject.nrn);
    }, [selectedProject, applyFilingTarget, loadSubfolders]);

    if (!isMailboxVersionSupported) {
        return (
            <div data-testid="send-and-file" className={styles.root}>
                <InfoComponent
                    icon="update-needed.svg"
                    primaryMessage={String(props.translate("SEND_AND_FILE.VERSION_ERROR_PRIMARY"))}
                    secondaryMessage={String(props.translate("SEND_AND_FILE.VERSION_ERROR_SECONDARY"))}
                />
            </div>
        );
    }

    Log.info(`myProjects [ ${myProjects.length} ] globalProjects [ ${globalProjects.length} ]`);
    const filterString = projectFilterString.toLocaleLowerCase();
    const isDarkTheme = props.theme === "dark";

    return (
        <div data-testid="send-and-file" className={styles.root}>
            {props.isGlobalProjectSearchSupported ? (
                <>
                    <div>
                        <Text size={300}>{props.translate("SEND_AND_FILE_NEW.FILING_HINT")}</Text>
                        <SearchSection
                            isDarkTheme={isDarkTheme}
                            isGlobalProjectSearchSupported={!!props.isGlobalProjectSearchSupported}
                            isLoadingMyProjects={isLoadingMyProjects}
                            isLoadingGlobalProjects={isLoadingGlobalProjects}
                            projectFilterString={projectFilterString}
                            myProjects={myProjects}
                            projectsService={props.projectsService}
                            showAllGlobalProjects={props.useGlobalProjects}
                            onSearchChange={(value) => onSearchChange(undefined, value)}
                            onSearchClear={onSearchClear}
                            onSearchResults={onServerSearchResults}
                            onRefresh={onRefresh}
                        />
                    </div>
                    <ProjectRadiosFluent9
                        isDarkTheme={isDarkTheme}
                        useGlobalProjects={props.useGlobalProjects}
                        isGlobalProjectSearchSupported={!!props.isGlobalProjectSearchSupported}
                        filterString={filterString}
                        suggestedProjects={suggestedProjects}
                        myProjects={myProjects}
                        globalProjects={globalProjects}
                        serverSearchResults={serverSearchResults}
                        isServerSearching={isServerSearching}
                        isLoadingMyProjects={isLoadingMyProjects}
                        selectedItem={selectedProject}
                        onSelectionChanged={handleProjectSelectionChanged}
                        itemLabel={getProjectDisplayName}
                        isProjectFilterMatch={getIsProjectFilterMatch}
                    />
                </>
            ) : (
                <SendAndFilePaginatedProjectArea
                    isDarkTheme={isDarkTheme}
                    useGlobalProjects={props.useGlobalProjects}
                    myProjects={myProjects}
                    globalProjects={globalProjects}
                    suggestedProjects={suggestedProjects}
                    isLoadingMyProjects={isLoadingMyProjects}
                    isLoadingGlobalProjects={isLoadingGlobalProjects}
                    isReloading={isUserReloadRef.current && (isLoadingMyProjects || isLoadingGlobalProjects)}
                    selectedProject={selectedProject}
                    onProjectSelected={handleProjectSelectionChanged}
                    onRefresh={onRefresh}
                    itemLabel={getProjectDisplayName}
                    isProjectFilterMatch={getIsProjectFilterMatch}
                />
            )}
            {selectedProject && props.isFileEmailIntoSubfolderSupported && subfolders.length > 0 && (
                <div className={styles.subfolderSection} data-testid="subfolder-section">
                    <div data-testid="subfolder-toggle-row">
                        <NewformaSwitch
                            checked={isSubfolderEnabled}
                            disabled={isLoadingMyProjects}
                            onChange={(_, data) => handleSubfolderToggleChanged(data.checked)}
                            className={styles.subfolderSwitch}
                            dataTestId="subfolder-toggle-switch"
                            labelPosition="before"
                            label={<Text>{props.translate("SEND_AND_FILE_NEW.SUBFOLDER_TOGGLE_LABEL") as string}</Text>}
                        />
                    </div>
                    {isSubfolderEnabled && (
                        <SubfolderPickerFluent9
                            folders={subfolders}
                            selectedFolder={selectedSubfolder}
                            onFolderSelected={handleSubfolderSelected}
                            onReload={handleReloadSubfolders}
                            isLoading={isReloadingSubfolders}
                            hideLabel
                            className={styles.subfolderPicker}
                            theme={props.theme}
                        />
                    )}
                </div>
            )}
            <NewformaLink
                className={styles.clearButton}
                dataTestId="clear-selection-button"
                disabled={!selectedProject || isLoadingMyProjects}
                onClick={() => handleProjectSelectionChanged(null)}
            >
                {props.translate("SEND_AND_FILE_NEW.CLEAR_SELECTION") as string}
            </NewformaLink>
        </div>
    );
};

interface SearchSectionProps extends LocalizeContextProps {
    isDarkTheme: boolean;
    isGlobalProjectSearchSupported?: boolean;
    isLoadingMyProjects: boolean;
    isLoadingGlobalProjects: boolean;
    projectFilterString: string;
    myProjects: Project[];
    projectsService: IProjectsService;
    showAllGlobalProjects: boolean;
    onSearchChange: (value: string) => void;
    onSearchClear: () => void;
    onSearchResults: (results: IProjectData[], isSearching: boolean) => void;
    onRefresh: () => void;
}

const SearchSection = withLocalize(
    (props: SearchSectionProps): JSX.Element => {
        const styles = useStyles();
        const prefixed = (key: string) => props.translate(`SEND_AND_FILE_NEW.${key}`) as string;
        const isLoading = props.isLoadingMyProjects || props.isLoadingGlobalProjects;

        return (
            <div data-testid="search-section" className={styles.searchContainer}>
                <div className={styles.searchInputRow}>
                    <SendAndFileProjectSearch
                        projectsService={props.projectsService}
                        myProjects={props.myProjects}
                        isLoadingMyProjects={props.isLoadingMyProjects}
                        showAllGlobalProjects={!!props.isGlobalProjectSearchSupported && props.showAllGlobalProjects}
                        filterString={props.projectFilterString}
                        onSearchChange={props.onSearchChange}
                        onSearchClear={props.onSearchClear}
                        onSearchResults={props.onSearchResults}
                        translate={(key) => prefixed(key)}
                        theme={props.isDarkTheme ? "dark" : "light"}
                        disabled={props.isLoadingMyProjects}
                    />
                </div>
                <div className={styles.searchHintRow}>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                        {prefixed("USE_SEARCH")}
                    </Text>
                </div>
                <div className={styles.refreshLinkRow}>
                    <NewformaLink
                        disabled={isLoading}
                        onClick={props.onRefresh}
                        className={styles.refreshLink}
                        dataTestId="reload-projects-button"
                    >
                        <ArrowClockwise20Regular />
                        {prefixed("RELOAD_PROJECTS")}
                    </NewformaLink>
                </div>
            </div>
        );
    }
);

export default withLocalize(SendAndFileFluent9);
